export class Model {
  static nativeTypes = [
    String,
    Number,
    Boolean,
    Array,
    Object,
    Function,
    Symbol
  ];

  constructor(props, fields) {
    this.validate(fields, props);
    this.mapParametersToModel(fields, props);
  }

  /**
   * Runs validation on all props. Custom validation is used if the
   * user passes in a validator along with their parameter value.
   * Otherwise, simple validation is used to make sure there is any
   * value for that parameter and that its type is correct.
   *
   * @param fields      Object
   * @param props       Object
   */
  validate(fields, props) {
    if (props && props.constructor === Object) {
      Object.keys(props).forEach(key => {
        const prop = props[key];
        // If the prop is required but not found in the fields object, throw an exception
        if (
          prop.required &&
          (!fields.hasOwnProperty(key) || typeof fields[key] === "undefined")
        ) {
          throw `${this.constructor.name} does not have a value for the required prop '${key}'.`;
        } else {
          if (
            prop.type &&
            fields &&
            fields[key] &&
            fields[key].constructor !== prop.type
          ) {
            if (Model.nativeTypes.indexOf(prop.type) > -1) {
              throw `${this.constructor.name} does not have the correct type for the prop '${key}'.`;
            } else if (!prop.type.prototype || !prop.type.constructor.name) {
              throw `${this.constructor.name} does not have a type with a constructor/that can be instantiated for the prop '${key}'.`;
            }
          }
          if (
            fields &&
            prop.hasOwnProperty("validator") &&
            typeof prop.validator === "function"
          ) {
            this.validateCustom(key, fields[key], prop.validator);
          } else if (
            prop.hasOwnProperty("validator") &&
            typeof prop.validator !== "function"
          ) {
            throw `${this.constructor.name}'s 'validator' prop should be a function.`;
          }
        }
      });
    }
  }

  /**
   * Runs a user-provided custom validator on the given parameter.
   *
   * @param key         String
   * @param value       Any
   * @param validator   () => Boolean
   */
  validateCustom(key, value, validator) {
    if (value == null) {
      throw `${this.constructor.name} failed the custom validation because it does not have a value for ${key}. Got ${value}.`;
    }

    if (!validator(value)) {
      throw `${this.constructor.name} parameter ${key} failed custom validation. Got ${value}.`;
    }
  }

  /**
   * Dynamically maps all the parameters to the model
   *
   * @param parameters    Object
   */
  mapParametersToModel(parameters, props) {
    if (props && props.constructor === Object) {
      Object.keys(props).forEach(key => {
        const prop = props[key];
        if (
          parameters &&
          parameters.constructor === Object &&
          parameters[key]
        ) {
          if (
            Model.nativeTypes.indexOf(prop.type) > -1 ||
            (parameters[key] && parameters[key].constructor.name !== "Object")
          ) {
            if (
              prop.type === Date &&
              parameters[key].constructor.name !== "Date"
            ) {
              this[key] = new Date(parameters[key]);
            } else {
              this[key] = parameters[key];
            }
          } else {
            this[key] = new prop.type(parameters[key]);
          }
        } else {
          if (prop.hasOwnProperty("default")) {
            this[key] = prop.default;
          } else {
            this[key] = undefined;
          }
        }
      });
    }
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  assignProp(key, value) {
    this[key] = value;
    return this;
  }
}
